import React from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X, Newspaper, Share2 } from "lucide-react";
import { toast } from "sonner";

const TONE_META = {
  fiducia:       { label: "FIDUCIA",   color: "#0ea5e9", emoji: "💪", gradient: "linear-gradient(135deg,#38bdf8,#0284c7)" },
  rabbia:        { label: "SFOGO",     color: "#ef4444", emoji: "🔥", gradient: "linear-gradient(135deg,#f97316,#dc2626)" },
  ironia:        { label: "SFOTTÒ",    color: "#f59e0b", emoji: "😏", gradient: "linear-gradient(135deg,#fbbf24,#f59e0b)" },
  dichiarazione: { label: "ESCLUSIVA", color: "#3b56e0", emoji: "📣", gradient: "linear-gradient(135deg,#6366f1,#3b56e0)" },
};

const initials = (n = "") => n.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("") || "FIFE";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function publicFileUrl(imagePath) {
  if (!imagePath) return null;
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) return imagePath;
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return null;
  return `${base}/api/public/files/${imagePath}`;
}

export default function InterviewModal({ interview, onClose }) {
  const open = !!interview;
  const t = interview ? (TONE_META[interview.tone] || TONE_META.dichiarazione) : TONE_META.dichiarazione;
  const img = interview?.image_path ? publicFileUrl(interview.image_path) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent hideClose className="bg-white border-slate-200 text-slate-900 max-w-2xl p-0 overflow-hidden max-h-[92vh] flex flex-col" data-testid="interview-modal">
        <DialogTitle className="sr-only">
          {interview ? `${interview.team_name || "Intervista"}: ${interview.headline || ""}` : "Intervista"}
        </DialogTitle>
        {interview && (
          <div className="flex flex-col min-h-0 overflow-hidden">
            {/* Cover: real image if provided, otherwise poster with initials */}
            <div className="relative aspect-[16/9] w-full overflow-hidden shrink-0" style={{ background: t.gradient }} data-testid="interview-cover">
              {img ? (
                <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <>
                  <div className="absolute inset-0 opacity-20"
                       style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0 12px, transparent 12px 28px)" }} />
                  <div className="absolute inset-0 flex items-center justify-center display font-black text-white text-7xl md:text-8xl"
                       style={{ textShadow: "0 4px 24px rgba(0,0,0,0.35)" }}>
                    {initials(interview.team_name)}
                  </div>
                </>
              )}
              <div className="absolute top-3 left-3 flex items-center gap-2 rounded-full px-3 py-1.5 bg-black/40 backdrop-blur">
                <Newspaper className="w-3.5 h-3.5 text-white" />
                <span className="text-[10px] font-black uppercase tracking-widest text-white">Sala stampa FIFE</span>
              </div>
              <button onClick={onClose} className="absolute top-3 right-3 rounded-full p-2 bg-white/90 hover:bg-white text-slate-800 shadow-md ring-1 ring-black/5" data-testid="interview-modal-close" aria-label="Chiudi">
                <X className="w-4 h-4" />
              </button>
              <button onClick={async () => {
                        // Usa window.location.origin per garantire che il link
                        // punti allo stesso host da cui l'utente lo genera.
                        const url = `${window.location.origin}/api/public/share/news/${interview.id}`;
                        const data = {
                          title: `${interview.team_name} · FIFE`,
                          text: `«${interview.headline}» — ${interview.coach_name || interview.team_name}`,
                          url,
                        };
                        // Incrementa contatore (best-effort)
                        try {
                          const { api } = await import("@/lib/api");
                          api.post(`/public/interviews/${interview.id}/share`).catch(() => {});
                        } catch {}
                        try {
                          if (navigator.share) await navigator.share(data);
                          else if (navigator.clipboard) { await navigator.clipboard.writeText(url); toast.success("Link copiato negli appunti"); }
                          else window.prompt("Copia il link:", url);
                        } catch (err) {
                          if (err && err.name !== "AbortError") toast.error("Condivisione non riuscita");
                        }
                      }}
                      className="absolute top-3 right-14 rounded-full p-2 bg-white/90 hover:bg-white text-slate-800 shadow-md ring-1 ring-black/5 transition hover:scale-105"
                      data-testid="interview-modal-share" aria-label="Condividi" title="Condividi">
                <Share2 className="w-4 h-4" />
              </button>
              <div className="absolute bottom-3 left-3">
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-white"
                      style={{ background: t.color }}>
                  {t.emoji} {t.label}
                </span>
              </div>
            </div>

            <div className="px-6 md:px-8 py-6 md:py-8 overflow-y-auto">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">
                {interview.team_name} · Girone {interview.girone} · {formatDate(interview.created_at)}
              </div>
              <h2 className="display font-black text-2xl md:text-3xl mt-2 leading-tight text-slate-900 break-words" data-testid="interview-modal-headline">
                «{interview.headline}»
              </h2>
              <p className="mt-4 text-slate-800 whitespace-pre-line leading-relaxed break-words [overflow-wrap:anywhere]" data-testid="interview-modal-quote">
                {interview.quote}
              </p>
              {interview.coach_name && (
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-black" style={{ background: t.color }}>
                    {initials(interview.coach_name)}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{interview.coach_name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Fantallenatore</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
